import { useMemo, useState } from "react";
import { Bell } from "lucide-react";
import { Card } from "../../ui/card";
import { ErrorCard, LoadingBlock, Pill } from "../AdminBits";
import { AdminTable, FilterTabs, ListFrame, MoreFooter } from "../ListBits";
import useAdminFetch from "../../../hooks/useAdminFetch";
import useAdminList from "../../../hooks/useAdminList";
import { NOTIF_CHANNEL_LABELS, NOTIF_STATUS } from "../../../lib/jamoraAdmin";
import { formatDateTime } from "../../../lib/tickets";

const STATUS_TABS = [["", "Tous"], ["sent", "Envoyés"], ["failed", "Échecs"], ["partial", "Partiels"], ["pending", "En cours"], ["skipped", "Ignorés"]];

const recipientsText = ({ total, failed }) => (total ? `${total} destinataire${total > 1 ? "s" : ""}${failed ? ` · ${failed} en échec` : ""}` : "—");
const onOff = (value, label) => <Pill tone={value ? "emerald" : "slate"}>{label}</Pill>;

// The notification rules of a pharmacy (which events send what, to how many people) and the send journal. Texts and numbers stay on Jamora.
export default function NotificationsTab({ schema }) {
  const rules = useAdminFetch(`/pharmacies/${schema}/notification-rules`);
  const [status, setStatus] = useState("");
  const list = useAdminList(`/pharmacies/${schema}/notification-logs`, { status });
  const labels = useMemo(() => Object.fromEntries((rules.data?.items || []).map((r) => [r.event, r.label])), [rules.data]);

  const logColumns = [
    { id: "event", label: "Événement", primary: true, cell: (l) => labels[l.event] || l.event },
    { id: "channel", label: "Canal", cell: (l) => NOTIF_CHANNEL_LABELS[l.channel] || l.channel },
    { id: "status", label: "Statut", cell: (l) => { const [text, tone] = NOTIF_STATUS[l.status] || [l.status, "slate"]; return <Pill tone={tone}>{text}</Pill>; } },
    { id: "recipients", label: "Destinataires", cell: (l) => recipientsText(l.recipients) },
    { id: "attempts", label: "Tentatives", hide: "lg", cell: (l) => l.attempts },
    { id: "error", label: "Erreur", hide: "lg", cell: (l) => l.error || "—" },
    { id: "date", label: "Date", cell: (l) => formatDateTime(l.created_at) },
  ];
  const ruleColumns = [
    { id: "label", label: "Événement", primary: true, cell: (r) => r.label },
    { id: "whatsapp", label: "WhatsApp", cell: (r) => onOff(r.whatsapp, r.whatsapp ? "Activé" : "Désactivé") },
    { id: "email", label: "E-mail", cell: (r) => onOff(r.email, r.email ? "Activé" : "Désactivé") },
    { id: "in_app", label: "Dans l'application", hide: "lg", cell: (r) => onOff(r.in_app, r.in_app ? "Activé" : "Désactivé") },
    { id: "who", label: "Destinataires", cell: (r) => (r.explicit_recipients ? `${r.explicit_recipients} choisi(s)` : "Tous les titulaires") },
    { id: "template", label: "Modèle", hide: "lg", cell: (r) => (r.custom_template ? "Personnalisé" : "Par défaut") },
  ];
  return (
    <div className="space-y-6" data-testid="pharmacy-notifications">
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-900">Règles par événement</h2>
        {rules.phase === "loading" || rules.phase === "idle" ? <LoadingBlock rows={3} />
          : rules.phase === "error" ? <ErrorCard message={rules.error} onRetry={rules.reload} testid="rules-error" />
            : !rules.data.items.length ? <Card className="p-4 text-sm text-slate-500" data-testid="rules-empty">Aucune règle enregistrée pour cette pharmacie.</Card>
              : <AdminTable columns={ruleColumns} rows={rules.data.items} rowKey={(r) => r.event} testid="rules-table" />}
      </section>
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-900">Journal d'envoi</h2>
        <FilterTabs label="Statut d'envoi" tabs={STATUS_TABS} value={status} onChange={setStatus} testid="notif-status" />
        <ListFrame list={list} rows={3} empty={{ icon: Bell, title: "Aucun envoi", hint: status ? "Essayez un autre statut." : "Aucune notification n'a encore été envoyée.", testid: "notif-empty" }}>
          <AdminTable columns={logColumns} rows={list.items} rowKey={(l) => l.id} testid="notif-table" />
          <MoreFooter list={list} noun="envoi(s)" testid="notif" />
        </ListFrame>
      </section>
    </div>
  );
}
